import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Crown, Zap, Code2, ArrowLeft, Gift, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ThemeToggle';
import { useAuthStore } from '../store/auth-store';
import { useSubscription } from '../lib/hooks/useSubscription';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { sendSubscriptionWebhook } from '../lib/webhooks';

const VYPAY_KEYS = {
  pro: 'ffad80342b816db0e9a8edc8d0705f1a',
  premium: '27d6bccf14c1404c599861c6475ca575',
  pro_gift: '1c63a6c63f7e06d9b237c224e672cd74',
  premium_gift: '33fbc0dcba683bb9ac65bb6260684b48'
};

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { subscription } = useSubscription();

  const PLANS = [
    {
      name: 'Free',
      price: '0',
      features: [
        '8 notes',
        '500 KB total storage',
        '5,000 character limit',
      ],
      button: {
        text: subscription === 'free' ? 'Current Plan' : 'Not Available',
        href: null,
        disabled: true
      }
    },
    {
      name: 'Pro',
      price: '19.99',
      features: [
        '20 notes',
        '5 MB total storage',
        '8,000 character limit',
        '5 custom tags',
        'Priority support'
      ],
      button: {
        text: subscription === 'pro' ? 'Current Plan' :
              subscription === 'premium' ? 'Not Available' : 'Upgrade to Pro',
        href: `https://vypay.xyz/pay.php?key=${VYPAY_KEYS.pro}`,
        disabled: subscription === 'pro' || subscription === 'premium'
      },
      giftButton: {
        href: `https://vypay.xyz/pay.php?key=${VYPAY_KEYS.pro_gift}`
      }
    },
    {
      name: 'Premium',
      price: '49.99',
      features: [
        '35 notes',
        '15 MB total storage',
        '10,000 character limit',
        '15 custom tags',
        'Team collaboration',
        'API Access',
        'Dedicated support'
      ],
      button: {
        text: subscription === 'premium' ? 'Current Plan' : 'Upgrade to Premium',
        href: `https://vypay.xyz/pay.php?key=${VYPAY_KEYS.premium}`,
        disabled: subscription === 'premium'
      },
      giftButton: {
        href: `https://vypay.xyz/pay.php?key=${VYPAY_KEYS.premium_gift}`
      }
    }
  ];

  useEffect(() => {
    const handlePaymentVerification = async () => {
      const referer = document.referrer;

      if (!referer.includes('vypay.xyz')) {
        console.error('Invalid payment referrer');
        navigate('/pricing');
        return;
      }

      try {
        const expiryDate = new Date();
        expiryDate.setMonth(expiryDate.getMonth() + 1);
        const newSubscription = location.pathname.includes('premium') ? 'premium' : 'pro';
        const amount = location.pathname.includes('premium') ? 49.99 : 19.99;

        const { error } = await supabase
          .from('user_settings')
          .update({
            subscription: newSubscription,
            subscription_expiry: expiryDate.toISOString()
          })
          .eq('id', user?.id);

        if (error) throw error;

        try {
          if (user) {
            await sendSubscriptionWebhook({
              email: user.email || 'N/A',
              name: user.user_metadata?.name || 'N/A',
              avatar_url: user.user_metadata?.avatar_url || undefined
            }, newSubscription, amount);
          }
        } catch (webhookError) {
          console.error("Failed to send subscription webhook:", webhookError);
        }

        toast.success(`Welcome to CodeNest ${newSubscription.charAt(0).toUpperCase() + newSubscription.slice(1)}! 🎉`);
        navigate('/dashboard');
      } catch (error: any) {
        console.error('Payment verification failed:', error);
        toast.error('Failed to verify payment. Please contact support.');
        navigate('/pricing');
      }
    };

    if (location.pathname.includes('codenest-pro') || location.pathname.includes('codenest-premium')) {
      handlePaymentVerification();
    }
  }, [location.pathname, user?.id, navigate, user?.email, user?.user_metadata?.name, user?.user_metadata?.avatar_url]);

  const handlePlanAction = (href: string | null) => {
    if (!user) {
      toast.error('Please sign in to upgrade your plan');
      navigate('/auth');
      return;
    }

    if (href) {
      window.location.href = href;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between">
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2 inline-block align-middle" />
              <span className="inline-block align-middle">Back</span>
            </Link>
          </Button>
          <ThemeToggle />
        </div>
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold mb-4"
          >
            Choose Your Plan
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground"
          >
            Select the perfect plan for your needs
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {PLANS.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative overflow-hidden rounded-2xl border bg-card p-8 ${
                plan.name === 'Premium' ? 'md:scale-105' : ''
              }`}
            >
              {plan.name === 'Premium' && (
                <div className="absolute top-0 right-0 bg-gradient-to-br from-purple-500 to-primary px-3 py-1 text-white text-sm font-medium rounded-bl-lg">
                  Popular
                </div>
              )}

              <div className="mb-6">
                {plan.name === 'Premium' ? (
                  <Crown className="w-12 h-12 text-purple-500" />
                ) : plan.name === 'Pro' ? (
                  <Zap className="w-12 h-12 text-primary" />
                ) : (
                  <Code2 className="w-12 h-12 text-muted-foreground" />
                )}
              </div>

              <h3 className="text-2xl font-bold mb-2">CodeNest {plan.name}</h3>
              <div className="flex items-baseline mb-6">
                <span className="text-3xl font-bold">{plan.price}</span>
                <span className="text-muted-foreground ml-1">VYC/month</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2">
                {plan.button.href ? (
                  <button
                    onClick={() => handlePlanAction(plan.button.href)}
                    className={`w-full inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors
                      ${plan.name === 'Premium'
                        ? 'bg-purple-500 text-white hover:bg-purple-600'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      } ${plan.button.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {plan.button.text}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                ) : (
                  <Button
                    className="w-full"
                    disabled={plan.button.disabled}
                  >
                    {plan.button.text}
                  </Button>
                )}

                {plan.giftButton && (
                  <a
                    href={plan.giftButton.href}
                    className={`w-full inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors
                      ${plan.name === 'Premium'
                        ? 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'}`}
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Gift {plan.name}
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
